import { StaticPageLayout } from '../layouts/StaticPageLayout'
import { H2, TeamMemberCard } from '../components/ui'
import { SEO } from '../components/SEO/SEO'

const TEAM_MEMBERS = [
  {
    name: 'Richard Ronayne',
    role: 'Co-Founder',
    image: '/media/Richard.png',
    description: [
      'Richard is a neuroscientist at the Queensland Brain Institute, with a background spanning computational neuroscience, electrical engineering, psychology, and machine learning.',
      'He has worked inside the research process long enough to understand precisely where it breaks down — and built the foundational architecture of the Wisdom Layer to fix it.',
    ],
  },
  {
    name: 'Jayce Rushton',
    role: 'Co-Founder',
    image: '/media/Jayce.png',
    description: [
      'Jayce is a mathematics and science graduate from the University of Queensland, with deep expertise in machine learning, LLM development, and mathematical modelling.',
      'He designed the statistical and geometric systems that make the Wisdom Layer\'s confidence scoring, navigational index, and cross-domain discovery capabilities possible at scale.',
    ],
  },
]

export const About = () => {
  const seoConfig = {
    title: 'About ET AI — Science Has a Trust Problem. We Solved It.',
    description:
      'ET AI is a continuously audited, conflict-aware knowledge system built from the scientific literature. Every claim carries a confidence score. Every conflict is surfaced.',
    keywords:
      'about ET AI, ET AI Labs, scientific AI, research tool, Richard Ronayne, Jayce Rushton, Wisdom Layer, epistemic AI',
    url: 'https://etai.app/about',
    canonical: 'https://etai.app/about',
    type: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'ET AI Labs',
        description:
          'Developers of ET AI, a continuously audited, conflict-aware knowledge system built from the scientific literature.',
        url: 'https://etai.app',
        foundingDate: '2023',
        founders: [
          {
            '@type': 'Person',
            name: 'Richard Ronayne',
            jobTitle: 'Co-Founder',
          },
          {
            '@type': 'Person',
            name: 'Jayce Rushton',
            jobTitle: 'Co-Founder',
          },
        ],
      },
    },
  }

  return (
    <StaticPageLayout title="About ET AI">
      <SEO {...seoConfig} />
      <div className="bg-dark-secondary rounded-lg border border-dark-border p-8">
        <div className="prose prose-invert max-w-none">
          {/* Trust Problem Section */}
          <section className="mb-12">
            <H2>Science Has a Trust Problem. We Solved It.</H2>
            <div className="space-y-4 text-gray-300">
              <p>
                The rise of AI research tools promised to accelerate scientific discovery. It
                delivered something else: faster access to unreliable information, synthesised with
                complete confidence.
              </p>
              <p>
                The problem was never AI hallucination. The problem is that the literature itself is
                broken. Only 11% of landmark cancer papers replicate. Half of published psychology
                papers contain statistical errors. Over 50% of preclinical research findings cannot
                be reproduced. Every AI tool reading this literature treats it as ground truth — and
                builds on sand.
              </p>
              <p className="font-semibold text-gray-100">
                ET AI exists because published does not mean true.
              </p>
            </div>
          </section>

          {/* Wisdom Layer Section */}
          <section className="mb-12">
            <H2>The Wisdom Layer</H2>
            <div className="space-y-4 text-gray-300">
              <p>
                We built something that has never existed before: a continuously audited,
                conflict-aware knowledge system built from the scientific literature, where every
                claim is assessed on its actual epistemic merit.
              </p>
              <p className="font-semibold text-gray-100">We call it the Wisdom Layer.</p>
              <p>
                Where other tools retrieve and summarise, ET AI analyses, audits, and judges. Every
                scientific claim in our system carries a derived confidence score — computed from
                replication count, effect sizes, methodology quality, and statistical integrity.
                Claims are linked through explicit dependency chains. When a foundational finding is
                questioned, everything built on top of it is automatically surfaced and re-evaluated.
              </p>
              <p>
                This is not a knowledge graph. It is not a search index. It is the first system that
                knows how much to trust what science says — and why.
              </p>
            </div>
          </section>

          {/* In Practice Section */}
          <section className="mb-12">
            <H2>What This Means in Practice</H2>
            <div className="space-y-6 text-gray-300">
              <div className="bg-dark-primary rounded-lg p-6 border border-dark-border">
                <p className="font-semibold text-gray-100 mb-2">For a pharma R&amp;D team</p>
                <p>
                  Before committing to a drug target, query ET AI. In seconds you know that the
                  three foundational papers supporting the target have a combined confidence of 0.41
                  — two are single-lab findings that have never been replicated, and one relies on a
                  methodology that has since been seriously questioned. That conversation used to
                  take six months of manual due diligence. Now it takes thirty seconds.
                </p>
              </div>
              <div className="bg-dark-primary rounded-lg p-6 border border-dark-border">
                <p className="font-semibold text-gray-100 mb-2">For a clinical trial team</p>
                <p>
                  Before designing a trial around a biomarker, query ET AI. Know immediately whether
                  the preclinical evidence is solid, contested, or built on a chain of findings that
                  traces back to a single underpowered 2009 study.
                </p>
              </div>
              <div className="bg-dark-primary rounded-lg p-6 border border-dark-border">
                <p className="font-semibold text-gray-100 mb-2">For AI research tools</p>
                <p>
                  ET AI operates as infrastructure. When a tool like Claude Science reads 500 papers
                  to inform a decision, it can query our API first — and know, before synthesising
                  anything, how much confidence the underlying literature actually warrants.
                </p>
              </div>
            </div>
          </section>

          {/* Scale Section */}
          <section className="mb-12">
            <H2>The Scale</H2>
            <div className="space-y-4 text-gray-300">
              <p>
                ET AI has ingested over 15 million biomedical papers. Our agentic pipeline runs
                continuously, processing new literature as it is published, extracting claims,
                auditing conflicts, and updating confidence scores across the entire knowledge graph.
                Every day the system runs, the Wisdom Layer becomes deeper, more interconnected, and
                more valuable.
              </p>
              <p>
                Our current corpus covers cognitive neuroscience in depth, with active expansion
                across all of biomedicine. Enterprise clients access the Wisdom Layer via API, with
                custom domain overlays that allow proprietary internal research to be cross-referenced
                against published literature within a fully private tenant.
              </p>
            </div>
          </section>

          {/* Team Section */}
          <section className="mb-12">
            <H2>Built by Scientists Who Understand the Problem</H2>
            <p className="text-gray-300 mb-8">
              ET AI was founded in Brisbane, Australia, by two researchers who had spent years
              watching the scientific process fail in slow motion — not from lack of effort, but from
              lack of epistemic infrastructure.
            </p>
            <div className="grid gap-6 md:grid-cols-2">
              {TEAM_MEMBERS.map((member) => (
                <TeamMemberCard key={member.name} {...member} />
              ))}
            </div>
          </section>

          {/* Enterprise Enquiries Section */}
          <section className="mb-4">
            <H2>Enterprise Enquiries</H2>
            <div className="space-y-4 text-gray-300">
              <p>
                ET AI is available to pharmaceutical R&amp;D teams, clinical research organisations,
                and academic institutions via enterprise licence.
              </p>
              <p>
                To discuss access, integration, or partnership:{' '}
                <a
                  href="mailto:contact@etai.app"
                  className="text-gray-100 underline hover:text-white transition-colors"
                >
                  contact@etai.app
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </StaticPageLayout>
  )
}

export default About
